import { createFileRoute, getRouteApi, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { useGetStudioLogoImage } from '~entities/studios/api/get-studio-logo-image';
import { useCurrentStudio, useStudioStore } from '~entities/studios/model/studio-store';

import { EmailTemplateEditorPage } from '~features/email-editor';

import { useGetEmailTemplateQuery } from '~shared/api/email-template';
import { checkMinRoleValue } from '~shared/utils/role-guard';
import { UserRoles } from '~entities/users';

const PendingComponent = () => {
  return (
    <div className='flex h-screen items-center justify-center'>Loading....</div>
  );
};

const emailCustomizerRouteApi = getRouteApi('/_auth/_layout/email-customizer');

const emailCustomizerSearchSchema = z.object({
  returnTo: z.string().optional(),
  returnStep: z.coerce.number().int().min(0).max(5).optional(),
});

const RouteComponent = () => {
  const { returnTo, returnStep } = emailCustomizerRouteApi.useSearch();
  const studio = useCurrentStudio();
  const studioId = useStudioStore((state) => state.currentStudioId);
  const locationId = studio?.locationId ?? '';
  const {
    data: emailTemplateData,
    isPending: isPendingEmailTemplate,
    isFetching: isFetchingEmailTemplate,
  } = useGetEmailTemplateQuery();
  const {
    data: studioLogoImage,
    isPending: isPendingLogo,
    isFetching: isFetchingLogo,
  } = useGetStudioLogoImage({
    studioId: locationId,
  });

  const isLoading =
    !studioId ||
    isPendingEmailTemplate ||
    (!!locationId && (isPendingLogo || isFetchingLogo));

  if (isLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='size-6 animate-spin' />
      </div>
    );
  }

  const subject = emailTemplateData?.subject ?? '';
  const paragraph1 = emailTemplateData?.paragraph1 ?? '';
  const paragraph2 = emailTemplateData?.paragraph2 ?? '';
  const htmlContent =
    paragraph1 + (paragraph2 ? '<br />' + paragraph2 : '');

  return (
    <EmailTemplateEditorPage
      key={studioId}
      returnTo={returnTo}
      returnStep={returnStep}
      initialSubject={subject}
      initialStudioName={
        studioLogoImage?.studioName ?? studio?.subaccountName ?? 'Your Studio'
      }
      studioLogoUrl={studioLogoImage?.imageUrl}
      initialHtmlContent={htmlContent}
      initialParagraph1Image={emailTemplateData?.paragraph1ImageUrl ?? ''}
      initialParagraph2Image={emailTemplateData?.paragraph2ImageUrl ?? ''}
      isLoadingTemplate={isFetchingEmailTemplate && !emailTemplateData}
    />
  );
};

export const Route = createFileRoute('/_auth/_layout/email-customizer')({
  validateSearch: zodValidator(emailCustomizerSearchSchema),
  beforeLoad: async () => {
    const hasAccess = await checkMinRoleValue(UserRoles.ADMIN);
    if (!hasAccess) {
      return redirect({
        to: '/dashboard',
      });
    }
  },
  pendingComponent: PendingComponent,
  component: RouteComponent,
});
